// /src/engine/partLibrary.ts

import * as THREE from 'three';
// Three.js ships the LDraw loader in the examples bundle.
import { LDrawLoader } from 'three/examples/jsm/loaders/LDrawLoader.js';
import { LDrawConditionalLineMaterial } from 'three/examples/jsm/materials/LDrawConditionalLineMaterial.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { findPartDef } from './partsCatalog';
import { colorToHex } from './ldrawColors';
import { loadLdcadShadowConnectors } from './ldcadShadow';
import { LDRAW_BASE, LDRAW_SHADOW_BASE } from './ldrawConfig';

type Template = THREE.Object3D;

// LDraw parts are authored with -Y as "up" (right-handed).
// three.js uses +Y as "up". The conversion is a 180° rotation about X:
// (x, y, z) -> (x, -y, -z)
const LDRAW_TO_THREE = new THREE.Matrix4().makeScale(1, -1, -1);

export type PartLibraryOptions = {
  partsLibraryPath?: string; // default LDRAW_BASE
  shadowLibraryPath?: string; // default LDRAW_SHADOW_BASE (LDCad shadow library)
};

export class PartLibrary {
  private loader: LDrawLoader;
  private cache = new Map<string, Promise<Template>>();
  private placementOffsetCache = new Map<string, Promise<number>>();
  private materialCache = new Map<string, THREE.MeshPhysicalMaterial>();
  private opts: Required<PartLibraryOptions>;
  private fileMapReady: Promise<void> | null = null;
  private fileMap: Record<string, string> | null = null;

  constructor(opts?: PartLibraryOptions) {
    this.opts = {
      partsLibraryPath: opts?.partsLibraryPath ?? LDRAW_BASE,
      shadowLibraryPath: opts?.shadowLibraryPath ?? LDRAW_SHADOW_BASE,
    };
    this.loader = new LDrawLoader();
    this.loader.setPartsLibraryPath(this.opts.partsLibraryPath);

    // ✅ REQUIRED in newer three.js: LDrawLoader needs the conditional line material type set,
    // even if you later hide all line objects.
    this.loader.setConditionalLineMaterial(LDrawConditionalLineMaterial as any);
    // Gives the loader a sane default material library (non-fatal if you override materials later).
    (this.loader as any).addDefaultMaterials?.();
    
    // Prefer clean, crisp edges in editor mode.
    this.loader.smoothNormals = false;
  }

  async createPart(partFile: string, ldrawColor: number, colorHex?: string): Promise<THREE.Object3D> {
    const hex = colorHex ?? colorToHex(ldrawColor);
    const connectorsPromise = loadLdcadShadowConnectors(partFile, { basePath: this.opts.shadowLibraryPath });
    const template = await this.getTemplate(partFile);
    const inst = template.clone(true);
    // Clone keeps references to geometries, which is ideal.
    this.applyMaterial(inst, this.getMaterial(hex));

    // Attach real connector metadata (if present).
    // Non-fatal: if the shadow library isn't installed this resolves to [].
    try {
      const conns = await connectorsPromise;
      if (conns.length) (inst.userData as any).connectors = conns;
    } catch {
      // ignore
    }
    
    return inst;
  }

  async getPlacementOffsetY(partFile: string): Promise<number> {
    const key = (partFile ?? '').toLowerCase();
    if (!this.placementOffsetCache.has(key)) {
      this.placementOffsetCache.set(
        key,
        this.getTemplate(partFile)
          .then((tpl) => {
            const box = new THREE.Box3().setFromObject(tpl);
            if (box.isEmpty() || !Number.isFinite(box.min.y)) return 0;
            const offset = -box.min.y;
            return Number.isFinite(offset) ? offset : 0;
          })
          .catch(() => 0)
      );
    }
    return this.placementOffsetCache.get(key)!;
  }

  private getMaterial(hex: string): THREE.MeshPhysicalMaterial {
    const key = (hex ?? '#c0c0c0').toLowerCase();
    if (!this.materialCache.has(key)) {
      this.materialCache.set(
        key,
        new THREE.MeshPhysicalMaterial({
          color: new THREE.Color(key),
          roughness: 0.28,
          metalness: 0.0,
          clearcoat: 0.55,
          clearcoatRoughness: 0.25,
        })
      );
    }
    return this.materialCache.get(key)!;
  }

  private applyMaterial(obj: THREE.Object3D, mat: THREE.Material) {
    obj.traverse((c: THREE.Object3D) => {
      // LDrawLoader returns a mix of LineSegments and Mesh.
      if ((c as THREE.Mesh).isMesh) {
        const m = c as THREE.Mesh;
        m.material = mat;
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
  }

  private async getTemplate(partFile: string): Promise<Template> {
    const key = partFile.toLowerCase();
    if (!this.cache.has(key)) {
      this.cache.set(key, this.loadTemplate(partFile));
    }
    return this.cache.get(key)!;
  }

  private normalizePartRef(pf: string): string {
    return (pf ?? '').replace(/\\/g, '/').replace(/^\/+/, '').trim();
  }

  private encodePath(p: string): string {
    return p.split('/').map(encodeURIComponent).join('/');
  }

  private resolveRelPath(partFile: string): string {
    const pf = this.normalizePartRef(partFile);
    const key = pf.toLowerCase();
    const base = key.split('/').pop() ?? key;

    const mapped =
      this.fileMap?.[key] ||
      this.fileMap?.[base] ||
      this.fileMap?.[`parts/${base}`] ||
      this.fileMap?.[`p/${base}`];

    if (mapped) return mapped;

    // Reasonable fallbacks
    if (/^(parts|p)\//i.test(pf)) return pf;
    return `parts/${pf}`;
  }

  private async resolveUrl(partFile: string): Promise<string> {
    // Allow absolute URLs/paths to pass through
    if (/^https?:\/\//i.test(partFile ?? '')) return partFile;
    if ((partFile ?? '').startsWith('/')) return partFile;

    await this.ensureFileMap();
    const base = this.opts.partsLibraryPath.endsWith('/')
      ? this.opts.partsLibraryPath
      : this.opts.partsLibraryPath + '/';
    const rel = this.resolveRelPath(partFile);
    return `${base}${this.encodePath(rel)}`;
  }

  private async ensureFileMap() {
    if (this.fileMapReady) return this.fileMapReady;
    this.fileMapReady = (async () => {
      try {
        const base = this.opts.partsLibraryPath.endsWith('/') ? this.opts.partsLibraryPath : this.opts.partsLibraryPath + '/';
        const res = await fetch(`${base}filemap.json`, { cache: 'force-cache' as any });
        if (!res.ok) return;
        const map = await res.json();
        if (map && typeof map === 'object') {
          this.fileMap = map as any;
          // Helps the loader resolve subparts/primitives quickly without directory probing.
          (this.loader as any).setFileMap?.(map);
        }
      } catch {
        // Non-fatal; fallback probing will still work.
        this.fileMap = null;
      }
    })();
    return this.fileMapReady;
  }

  private async loadTemplate(partFile: string): Promise<Template> {
    // Try real LDraw first, but ALWAYS fail-safe to a fallback part.
    // Using loadAsync ensures thrown parser errors reject and we can catch them.
    try {
      // ✅ Resolve *top-level* parts through filemap.json when present.
      const url = await this.resolveUrl(partFile);
      const group = await (this.loader as any).loadAsync(url);

      // Hide edges/lines for the prototype (LDrawLoader can produce multiple line types).
      group.traverse((c: any) => {
        if (c?.isLine || c?.isLineSegments || c?.isLine2 || c?.isLineSegments2) {
          c.visible = false;
        }
      });

      // ✅ Fix upside-down parts: convert LDraw coords (-Y up) into three.js coords (+Y up).
      // IMPORTANT: we bake this into geometry inside optimizeGroup(), because SceneManager
      // overwrites the root matrix for placement (which would otherwise erase a root rotation).
      group.applyMatrix4(LDRAW_TO_THREE);

      return this.optimizeGroup(group);
    } catch {
      // Fall back to a simple box so the editor remains usable without the full library.
      return this.fallbackPart(partFile);
    }
  }

  private optimizeGroup(group: THREE.Object3D): THREE.Object3D {
    // Merge meshes to reduce draw calls (big perf win for real LDraw parts).
    // IMPORTANT: Always bake transforms into geometry so the returned template has an identity root
    // transform (SceneManager overwrites the root matrix for placement).
    const root = new THREE.Group();
    root.name = group.name || 'ldraw:part';

    const baked: THREE.BufferGeometry[] = [];

    group.updateMatrixWorld(true);
    group.traverse((c: THREE.Object3D) => {
      const m = c as THREE.Mesh;
      if (!m.isMesh) return;
      const geom = m.geometry as THREE.BufferGeometry;
      if (!geom) return;

      const g2 = geom.clone();
      m.updateMatrixWorld(true);
      g2.applyMatrix4(m.matrixWorld);
      baked.push(g2);
    });

    if (baked.length === 0) return group;

    // Best case: one merged mesh.
    try {
      const merged = mergeGeometries(baked, false);
      if (merged) {
        if (!merged.getAttribute('normal')) merged.computeVertexNormals();
        merged.computeBoundingBox();
        merged.computeBoundingSphere();

        for (const g of baked) {
          try { g.dispose(); } catch {}
        }

        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial({ color: 0xffffff }));
        root.add(mesh);
        return root;
      }
    } catch {
      // ignore and fall back to multi-mesh
    }

    // Fallback: keep multiple baked meshes (still correct orientation; just more draw calls).
    const mat = new THREE.MeshStandardMaterial({ color: 0xffffff });
    for (const g of baked) {
      const mesh = new THREE.Mesh(g, mat);
      root.add(mesh);
    }
    return root;
  }

  private fallbackPart(partFile: string): THREE.Object3D {
    const def = findPartDef(partFile) ?? { studs: { w: 2, d: 2, plates: 3 }, name: partFile };
    const w = def.studs.w * 20;
    const d = def.studs.d * 20;
    const h = def.studs.plates * 8;

    const group = new THREE.Group();
    group.name = `fallback:${partFile}`;

    const geom = new THREE.BoxGeometry(w, h, d);
    const mesh = new THREE.Mesh(geom, new THREE.MeshStandardMaterial({ color: 0x808080 }));
    mesh.position.y = h / 2;
    group.add(mesh);

    // Add a few studs for readability.
    const studR = 6;
    const studH = 2.2;
    const studGeom = new THREE.CylinderGeometry(studR, studR, studH, 16);
    const studs = new THREE.Group();
    for (let x = 0; x < def.studs.w; x++) {
      for (let z = 0; z < def.studs.d; z++) {
        const s = new THREE.Mesh(studGeom, new THREE.MeshStandardMaterial({ color: 0x9a9a9a }));
        s.position.set(
          -w / 2 + 10 + x * 20,
          h + studH / 2,
          -d / 2 + 10 + z * 20
        );
        studs.add(s);
      }
    }
    group.add(studs);
    return group;
  }
}
